import { DashboardApiFilters, fetchDashboardEndpoint, requestDashboardExport } from "../dashboardApi";

export type RevenuePeriod = "by_week" | "by_month" | "by_year";

export interface OverviewStat {
  key: string;
  display_name: string;
  amount: number;
  count: number | null;
  growth: string;
}

export interface ProductRevenue {
  product_name: string;
  product_revenue: number;
  growth: string;
}

export interface TeamRevenue {
  team_name: string;
  team_revenue: number;
  growth: string;
}

export interface TrendPoint {
  date: string;
  actual: number;
  prior: number;
}

export type RevenueTrend = Record<RevenuePeriod, TrendPoint[]>;

export interface ComparisonItem {
  key: string;
  value: number;
  previous: number;
  growth: string;
}

export interface DealValueStats {
  current: number;
  previous: number;
  growth: string;
}

export interface RevenueSummary {
  overviewStats: OverviewStat[];
  products: ProductRevenue[];
  trend: RevenueTrend;
  comparison: ComparisonItem[];
  teams: TeamRevenue[];
  dealValue: DealValueStats;
}

const PERIODS: RevenuePeriod[] = ["by_week", "by_month", "by_year"];

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toText = (value: unknown) => (value === null || value === undefined ? "" : String(value));

const growthOf = (raw: any) => {
  if (!raw || typeof raw !== "object") return "";
  const key = Object.keys(raw).find((field) => /growth_rate(_by_last_period)?$/.test(field));
  const value = key ? raw[key] : "";
  return typeof value === "object" ? toText(Object.values(value ?? {})[0]) : toText(value);
};

const normalizeTrendPoints = (raw: unknown): TrendPoint[] =>
  asArray<any>(raw).map((point) => ({
    date: toText(point?.date),
    actual: toNumber(point?.actual),
    prior: toNumber(point?.previous ?? point?.prior),
  }));

const COMPARISON_FIELDS: Record<string, { value: string; previous: string; growth: string }> = {
  closed_deals: {
    value: "deal_closed_count",
    previous: "deal_closed_count_from_last_period",
    growth: "deal_closed_count_growth_rate_by_last_period",
  },
  average_sales_cycle: {
    value: "average_sales_cycle",
    previous: "average_sales_cycle_from_last_period",
    growth: "average_sales_cycle_growth_rate_by_last_period",
  },
};

export const normalizeRevenueSummary = (raw: any): RevenueSummary => {
  const trendSource = raw?.revenue_performance_trend ?? {};
  const comparisonSource = raw?.deal_grouwth_rate ?? raw?.deal_growth_rate ?? {};
  const dealValue = raw?.deal_value ?? {};

  return {
    overviewStats: asArray<any>(raw?.overview_stats).map((stat) => ({
      key: toText(stat?.key),
      display_name: toText(stat?.display_name ?? stat?.key),
      amount: toNumber(stat?.value ?? stat?.amount),
      count: stat?.count === undefined || stat?.count === null ? null : toNumber(stat.count),
      growth: growthOf(stat),
    })),
    products: asArray<any>(raw?.revenue_breakdown_by_product_n_service ?? raw?.revenue_by_product_n_service).map(
      (product) => ({
        product_name: toText(product?.product_name),
        product_revenue: toNumber(product?.product_revenue),
        growth: growthOf(product),
      })
    ),
    trend: PERIODS.reduce(
      (acc, period) => ({ ...acc, [period]: normalizeTrendPoints(trendSource[period]) }),
      {} as RevenueTrend
    ),
    comparison: Object.entries(COMPARISON_FIELDS)
      .filter(([key]) => comparisonSource[key])
      .map(([key, fields]) => {
        const item = comparisonSource[key];
        return {
          key,
          value: toNumber(item?.[fields.value]),
          previous: toNumber(item?.[fields.previous]),
          growth: toText(item?.[fields.growth]),
        };
      }),
    teams: asArray<any>(raw?.revenue_by_team).map((team) => ({
      team_name: toText(team?.team_name),
      team_revenue: toNumber(team?.team_revenue),
      growth: growthOf(team),
    })),
    dealValue: {
      current: toNumber(dealValue.avg_deal_value_in_current_period),
      previous: toNumber(dealValue.avg_deal_value_from_last_period ?? dealValue.avg_deal_value_by_last_period),
      growth: toText(dealValue.avg_deal_value_growth_rate_by_last_period),
    },
  };
};

export const fetchRevenueSummary = async (
  tenantId: string | number,
  filters: DashboardApiFilters = {}
): Promise<RevenueSummary> =>
  normalizeRevenueSummary(
    await fetchDashboardEndpoint("/dashboard/revenue-summary/", tenantId, filters, "revenue summary")
  );

export const exportRevenueSummaryExcel = (tenantId: string | number, filters: DashboardApiFilters = {}) =>
  requestDashboardExport("/dashboard/revenue-summary/export/", tenantId, filters, "revenue summary");
